import { createHttpClient } from '@adapters/HttpClient';
import { ShopsApiAdapter } from '@modules/Orders/List/adapters/ShopsApiAdapter';
import { faXmark } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@shared/components/ui/button';
import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import { DateRangePicker } from '@shared/components/ui/DateRangePicker';
import { InfoTooltip } from '@shared/components/ui/InfoTooltip';
import { apiClientWH } from '@shared/services';
import { useCallback, useEffect, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

export interface ProfitabilityFiltersState {
  startDate?: string;
  endDate?: string;
  shopId: number | null;
}

interface ProfitabilityFiltersBarProps {
  value: ProfitabilityFiltersState;
  onChange: (next: ProfitabilityFiltersState) => void;
  getDefaultStartDate: () => string;
  getDefaultEndDate: () => string;
}

const formatDateLocal = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const ProfitabilityFiltersBar = ({
  value,
  onChange,
  getDefaultStartDate,
  getDefaultEndDate,
}: ProfitabilityFiltersBarProps) => {
  const { t } = useTranslation('profitabilityModule');

  const [localStartDate, setLocalStartDate] = useState<string | undefined>(value.startDate);
  const [localEndDate, setLocalEndDate] = useState<string | undefined>(value.endDate);
  const [localShopId, setLocalShopId] = useState<string>(value.shopId != null ? String(value.shopId) : '');

  const [shopOptions, setShopOptions] = useState<{ label: string; value: string }[]>([]);
  const [isLoadingShops, setIsLoadingShops] = useState(false);

  useEffect(() => {
    setLocalStartDate(value.startDate);
    setLocalEndDate(value.endDate);
    setLocalShopId(value.shopId != null ? String(value.shopId) : '');
  }, [value.startDate, value.endDate, value.shopId]);

  const loadShops = useCallback(async () => {
    try {
      setIsLoadingShops(true);
      const httpClient = createHttpClient(apiClientWH);
      const shopsAdapter = new ShopsApiAdapter(httpClient);
      const shops = await shopsAdapter.getAll();
      setShopOptions([
        { label: t('filters.all_shops'), value: '' },
        ...shops.map((shop) => ({ label: shop.name, value: String(shop.id) })),
      ]);
    } catch {
      setShopOptions([{ label: t('filters.all_shops'), value: '' }]);
    } finally {
      setIsLoadingShops(false);
    }
  }, [t]);

  useEffect(() => {
    loadShops();
  }, [loadShops]);

  const handleDateRangeUpdate = ({ range }: { range: DateRange }) => {
    if (!range.from || !range.to) return;
    const newStart = formatDateLocal(range.from);
    const newEnd = formatDateLocal(range.to);
    setLocalStartDate(newStart);
    setLocalEndDate(newEnd);
    onChange({
      startDate: newStart,
      endDate: newEnd,
      shopId: localShopId ? Number(localShopId) : null,
    });
  };

  const handleShopChange = (v: string) => {
    setLocalShopId(v);
    onChange({
      startDate: localStartDate,
      endDate: localEndDate,
      shopId: v ? Number(v) : null,
    });
  };

  const handleClearFilters = useCallback(() => {
    const ds = getDefaultStartDate();
    const de = getDefaultEndDate();
    setLocalStartDate(ds);
    setLocalEndDate(de);
    setLocalShopId('');
    onChange({
      startDate: ds,
      endDate: de,
      shopId: null,
    });
  }, [getDefaultStartDate, getDefaultEndDate, onChange]);

  return (
    <div
      className="mb-4 rounded-lg border border-border bg-card/40 p-3 transition-colors"
      data-testid="profitability-filters-section"
    >
      <div
        className="flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:items-end sm:gap-x-3 sm:gap-y-2"
        data-testid="profitability-filters"
      >
        <div className="w-full min-w-0 sm:w-[200px] sm:max-w-[240px] sm:flex-shrink-0">
          <label
            htmlFor="profitability-filter-shop"
            className="mb-1 block text-[11px] font-medium leading-none text-muted-foreground"
          >
            {t('filters.shops_label')}
          </label>
          <div id="profitability-filter-shop">
            <Combobox
              options={shopOptions}
              value={localShopId}
              setValue={handleShopChange}
              placeholder={t('filters.all_shops')}
              disabled={isLoadingShops}
              dataTestId="profitability-shop"
            />
          </div>
        </div>

        <div className="min-w-0 flex-1 sm:min-w-[260px]">
          <div className="mb-1 flex items-center gap-1">
            <label className="text-[11px] font-medium leading-none text-muted-foreground">
              {t('filters.dates_label')}
            </label>
            <InfoTooltip
              text={t('filters.creation_date_only')}
              dataTestId="profitability-creation-date-hint"
              iconClassName="h-3 w-3 text-muted-foreground"
            />
          </div>
          <DateRangePicker
            initialDateFrom={localStartDate}
            initialDateTo={localEndDate}
            onUpdate={handleDateRangeUpdate}
            align="start"
            showPresets
            triggerClassName="h-9 w-full border-border bg-background transition-colors"
            placeholder={t('filters.select_date_range')}
            dataTestId="profitability-date-range"
          />
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleClearFilters}
          className="h-9 w-full shrink-0 gap-1.5 border-border bg-background px-3 text-muted-foreground hover:bg-muted/50 hover:text-foreground sm:ml-auto sm:w-auto sm:self-end"
          data-testid="profitability-clear-filters"
        >
          <FontAwesomeIcon icon={faXmark} className="h-3 w-3" aria-hidden />
          {t('filters.clear')}
        </Button>
      </div>
    </div>
  );
};
